import React, {useContext} from "react";
import {createContext, ReactNode, useState} from "react";

export class EAIError {
    type: string;
    message: string;
    original_exception: Error;
    display: "inline" | "global";
    repeats: number;

    constructor(error: any, customMessage?: string){
        this.type = error.name || "Unknown Type";
        this.message = customMessage || error.response?.data?.result?.message || "An unknown error occurred";
        this.original_exception = error;
        this.display = "global";
        this.repeats = 0;
    }
}

export type AppContextType = {
    errors: EAIError[]
    alerts: Map<number, string>
    confirmModal: ModalOptions | null
    appTitle: string

    // similar to redux actions, it will help to change shared app state context
    dispatchError: (err: any, customMessage?: string) => void
    clearErrors: () => void
    addAlert: (message: string) => void
    removeAlert: (id: number) => void
    hideConfirmModal: () => void
    showConfirmModal: (opts: ModalOptions) => void
    setPageTitle: (appTitle: string) => void
}

export interface ModalOptions {
    element: JSX.Element,
    accept: () => void,
    cancel?: () => void,
    showActions: boolean,
    acceptText?: string,
    cancelText?: string,
}

export const AppContext = createContext<AppContextType>({
    errors: [],
    alerts: new Map(),
    confirmModal: null,
    appTitle: "Home",
    dispatchError: () => {},
    clearErrors: () => {},
    addAlert: () => {},
    removeAlert: () => {},
    hideConfirmModal: () => {},
    showConfirmModal: () => {},
    setPageTitle: () => {}
});

export function AppProvider({children}: {children: ReactNode}) {
    const [errors, setErrors] = useState<EAIError[]>([]);
    const [alerts, setAlerts] = useState<Map<number, string>>(new Map());
    const [confirmModal, setConfirmModal] = useState<ModalOptions | null>(null);
    const [pageTitle, setPageTitle] = useState("Home");
    const provider = {
        errors,
        alerts,
        confirmModal,
        appTitle: pageTitle,
        dispatchError: (err: Error, customMessage?: string) => {
            setErrors([...errors, new EAIError(err, customMessage)]);
        },
        clearErrors: () => {
            setErrors([]);
        },
        addAlert: (message: string) => {
            const id = Math.random();
            setAlerts(new Map(alerts.set(id, message)));
        },
        removeAlert: (id: number) => {
            alerts.delete(id);
            setAlerts(new Map(alerts));
        },
        hideConfirmModal: () => {
            setConfirmModal(null);
        },
        showConfirmModal: (opts: ModalOptions) => {
            setConfirmModal(opts);
        },
        setPageTitle: (pageTitle: string) => {
            setPageTitle(pageTitle);
        }
    };
    return <AppContext.Provider value={provider}>{children}</AppContext.Provider>;
}

export const useAppContext = () => useContext(AppContext);

